// src/components/collections/Collections.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./collections.css";
import {
  fetchSanklanRows,
  saveSanklan,
  applySanklanRates,
} from "../../services/sanklan";
import { useCustomerRepo, useRateRepo } from "../../repos/AppRepoProvider";
import CollectionAdjustment from "./CollectionAdjustment";
import AppDialog from "../common/AppDialog";

function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}

function todayDmy() {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// ---- Date picker helpers (local) ----
function todayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}

function dmyToIso(dmy) {
  const s = String(dmy || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  const dd = pad2(m[1]);
  const mm = pad2(m[2]);
  const yy = m[3];
  return `${yy}-${mm}-${dd}`;
}

function isoToDmy(iso) {
  const s = String(iso || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function isFutureIso(iso) {
  if (!iso) return false;
  return iso > todayIsoLocal();
}

function fmt2(v) {
  const n = Number(v || 0);
  return n.toFixed(2);
}
function fmt1(v) {
  const n = Number(v || 0);
  return n.toFixed(1);
}

function shiftShort(time) {
  const t = String(time || "").toLowerCase();
  if (t === "morning" || t === "m" || t.includes("सकाळ")) return "M";
  if (t === "evening" || t === "e" || t.includes("संध्या")) return "E";
  return "-";
}

function milkShort(type) {
  const t = String(type || "").toLowerCase();
  if (t === "cow" || t === "c" || t.includes("गाय")) return "C";
  if (t === "buffalo" || t === "buff" || t === "b" || t === "buf" || t.includes("म्हैस"))
    return "B";
  return "-";
}

// --- optional: match Android "fat+clr auto snf" formula ---
function round1(v) {
  return Math.round(v * 10) / 10;
}
function snfFromDigri(clr) {
  const steps = Math.floor((clr - 20.3) / 0.4);
  let snf = 6.0 + 0.1 * steps;
  if (snf < 5.9) snf = 5.9;
  return round1(snf);
}

// --- sanktype mode (best-effort) ---
function getSankTypeMode() {
  const raw = (localStorage.getItem("sanktypesettings") || "").trim();
  const s = raw.toLowerCase();

  if (s.includes("liter") && s.includes("only")) return "LITER_ONLY";
  if (s.includes("clr") && s.includes("only")) return "CLR_ONLY";
  if (s.includes("fat") && s.includes("only")) return "FAT_ONLY";
  if (s.includes("fat") && s.includes("clr") && s.includes("auto"))
    return "FAT_CLR_AUTO_SNF";
  if (s.includes("fat") && s.includes("clr")) return "FAT_CLR";
  if (s.includes("fat") && s.includes("snf")) return "FAT_SNF";

  return "FAT_SNF";
}

// ✅ make sure server receives English "Morning/Evening"
function mapTimeForServer(v) {
  const raw = String(v || "").trim();
  const t = raw.toLowerCase();

  if (t === "morning" || t === "m" || t.includes("सकाळ")) return "Morning";
  if (t === "evening" || t === "e" || t.includes("संध्या")) return "Evening";

  if (raw === "Morning" || raw === "Evening") return raw;
  return "Morning";
}

// ✅ make sure server receives English "Cow/Buffalo"
function mapTypeForServer(v) {
  const raw = String(v || "").trim();
  const t = raw.toLowerCase();

  if (t === "buffalo" || t === "buff" || t === "b" || t === "buf" || t.includes("म्हैस"))
    return "Buffalo";
  if (t === "cow" || t === "c" || t.includes("गाय")) return "Cow";

  if (raw === "Cow" || raw === "Buffalo") return raw;
  return "Cow";
}

function normalizeDmy(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return isoToDmy(s);
  return s;
}
function normalizeTimeTitle(v) {
  const t = String(v || "").trim().toLowerCase();
  if (t === "morning" || t === "m" || t.includes("सकाळ")) return "Morning";
  if (t === "evening" || t === "e" || t.includes("संध्या")) return "Evening";
  if (String(v) === "Morning" || String(v) === "Evening") return String(v);
  return "Morning";
}
function normalizeTypeTitle(v) {
  const t = String(v || "").trim().toLowerCase();
  if (t === "buffalo" || t === "buff" || t === "b" || t === "buf" || t.includes("म्हैस"))
    return "Buffalo";
  if (t === "cow" || t === "c" || t.includes("गाय")) return "Cow";
  if (String(v) === "Cow" || String(v) === "Buffalo") return String(v);
  return "Cow";
}

function safeD(v) {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
}
function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}
function numStrOrZero(v) {
  const s = String(v ?? "").trim();
  if (!s) return "0";
  const n = Number(s);
  if (!Number.isFinite(n)) return "0";
  return String(n);
}

export default function Collections({ mainuser, selectedKendra }) {
  const customers = useCustomerRepo();
  const rates = useRateRepo();

  // ✅ Update Rate dialog
  const [rateDlgOpen, setRateDlgOpen] = useState(false);
  const [rateDlgBusy, setRateDlgBusy] = useState(false);

  const [date, setDate] = useState(todayDmy());
  const [shift, setShift] = useState("All");
  const [milk, setMilk] = useState("All");

  const [tableSearch, setTableSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // pagination
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const kendramobno = selectedKendra?.mobno || "";

  // ---------------- Same-page view toggle ----------------
  const [view, setView] = useState("list"); // "list" | "add" | "adjust"
  const addOpen = view === "add";
  const adjOpen = view === "adjust";

  // ---------------- Edit mode ----------------
  const [editMode, setEditMode] = useState(false);
  const [editPk, setEditPk] = useState(null); // {kno,date,time,type} title-case + dd/mm/yyyy

  // ---------------- Add Collection form state ----------------
  const [fDate, setFDate] = useState(date);
  const [fTime, setFTime] = useState("Morning");
  const [fMilk, setFMilk] = useState("Cow");

  const [fKno, setFKno] = useState("");
  const [fCname, setFCname] = useState("");
  const [fSample, setFSample] = useState("1");

  const [fLiter, setFLiter] = useState("");
  const [fFat, setFFat] = useState("");
  const [fSnf, setFSnf] = useState("");
  const [fDigri, setFDigri] = useState("");
  const [fRate, setFRate] = useState("");

  const [rateExplain, setRateExplain] = useState("");
  const [saving, setSaving] = useState(false);

  const knoDebounceRef = useRef(null);

  function openAdjust() {
    setView("adjust");
  }
  function closeAdjust() {
    setView("list");
  }

  // ---- Enter-to-next focus refs ----
  const knoRef = useRef(null);
  const sampleRef = useRef(null);
  const literRef = useRef(null);
  const fatRef = useRef(null);
  const digriRef = useRef(null);
  const snfRef = useRef(null);
  const rateRef = useRef(null);
  const saveBtnRef = useRef(null);

  const sankMode = getSankTypeMode();

  const showFat =
    sankMode === "FAT_SNF" ||
    sankMode === "FAT_ONLY" ||
    sankMode === "FAT_CLR" ||
    sankMode === "FAT_CLR_AUTO_SNF";

  const showSnf = sankMode === "FAT_SNF" || sankMode === "FAT_CLR_AUTO_SNF";

  const showDigri =
    sankMode === "CLR_ONLY" ||
    sankMode === "FAT_CLR" ||
    sankMode === "FAT_CLR_AUTO_SNF";

  const rateEditable = sankMode === "LITER_ONLY";

  const fieldOrder = useMemo(() => {
    const o = ["kno", "sample", "liter"];
    if (showFat) o.push("fat");
    if (showDigri) o.push("digri");
    if (showSnf) o.push("snf");
    if (rateEditable) o.push("rate");
    o.push("save");
    return o;
  }, [showFat, showDigri, showSnf, rateEditable]);

  const fieldRefs = useMemo(
    () => ({
      kno: knoRef,
      sample: sampleRef,
      liter: literRef,
      fat: fatRef,
      digri: digriRef,
      snf: snfRef,
      rate: rateRef,
      save: saveBtnRef,
    }),
    []
  );

  const focusKey = useCallback(
    (k) => {
      const el = fieldRefs?.[k]?.current;
      if (!el) return;
      el.focus?.();
      try {
        el.select?.();
      } catch (e) {}
    },
    [fieldRefs]
  );

  const onEnterAdvance = useCallback(
    (e, key) => {
      if (e.key !== "Enter") return;
      e.preventDefault();

      const idx = fieldOrder.indexOf(key);
      const nextKey =
        idx === -1
          ? fieldOrder[0]
          : fieldOrder[Math.min(idx + 1, fieldOrder.length - 1)];

      if (key === "kno") {
        focusKey("sample");
        return;
      }
      focusKey(nextKey);
    },
    [fieldOrder, focusKey]
  );

  // when add view opens, auto focus Customer Code
  useEffect(() => {
    if (!addOpen) return;
    const t = setTimeout(() => focusKey("kno"), 0);
    return () => clearTimeout(t);
  }, [addOpen, focusKey]);

  function openAdd() {
    setEditMode(false);
    setEditPk(null);

    setFDate(date);
    setFTime("Morning");
    setFMilk("Cow");

    setFKno("");
    setFCname("");
    setFSample("1");

    setFLiter("");
    setFFat("");
    setFSnf("");
    setFDigri("");
    setFRate("");
    setRateExplain("");

    setView("add");
  }

  function closeAdd() {
    setView("list");
    setEditMode(false);
    setEditPk(null);
  }

  function openEdit(row) {
    const dmy = normalizeDmy(row?.date) || date;
    const timeT = normalizeTimeTitle(row?.time);
    const typeT = normalizeTypeTitle(row?.type);
    const kn = String(row?.kno ?? "").trim();

    setEditMode(true);
    setEditPk({ kno: kn, date: dmy, time: timeT, type: typeT });

    setFDate(dmy);
    setFTime(timeT);
    setFMilk(typeT);

    setFKno(kn);
    setFCname(String(row?.cname ?? row?.name ?? "").trim());

    setFSample(String(row?.sample ?? "1"));

    setFLiter(String(row?.liter ?? ""));
    setFFat(String(row?.fat ?? ""));
    setFSnf(String(row?.snf ?? ""));
    setFDigri(String(row?.digri ?? ""));
    setFRate(String(row?.rate ?? ""));
    setRateExplain("");

    setView("add");
    setTimeout(() => focusKey("liter"), 0);
  }

  async function loadData() {
    if (!mainuser) return;
    if (!kendramobno) {
      setRows([]);
      setErr("Select center");
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const list = await fetchSanklanRows({
        mainuser,
        kendramobno,
        date,
        shift,
        milk,
      });
      setRows(Array.isArray(list) ? list : []);
      setPage(1);
    } catch (e) {
      setErr(e?.message || "Network Error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainuser, kendramobno]);

  // keep fDate in sync when you change list date (nice UX) ONLY when not editing
  useEffect(() => {
    if (!addOpen) return;
    if (editMode) return;
    setFDate(date);
  }, [addOpen, date, editMode]);

  // kno -> cname lookup (debounced) (skip while edit if cname already present)
  useEffect(() => {
    if (!addOpen) return;

    if (knoDebounceRef.current) clearTimeout(knoDebounceRef.current);
    knoDebounceRef.current = setTimeout(() => {
      const kn = String(fKno || "").trim();
      if (!kn) {
        setFCname("");
        return;
      }

      if (!customers?.ready) {
        if (!editMode) setFCname("");
        return;
      }

      const name = customers.getNameByKno(kn);
      if (!editMode) setFCname(name || "");
      else if (!String(fCname || "").trim()) setFCname(name || "");
    }, 80);

    return () => {
      if (knoDebounceRef.current) clearTimeout(knoDebounceRef.current);
    };
  }, [addOpen, fKno, customers, editMode, fCname]);

  // auto SNF from digri when FAT+CLR_AUTO_SNF mode
  useEffect(() => {
    if (!addOpen) return;
    if (sankMode !== "FAT_CLR_AUTO_SNF") return;

    const clr = Number(fDigri);
    if (!Number.isFinite(clr) || clr <= 0) {
      setFSnf("");
      return;
    }
    setFSnf(String(snfFromDigri(clr).toFixed(1)));
  }, [addOpen, fDigri, sankMode]);

  // rate auto calc
  useEffect(() => {
    if (!addOpen) return;

    if (sankMode === "LITER_ONLY") {
      setRateExplain("");
      return;
    }

    const kn = String(fKno || "").trim();
    const milkType = fMilk === "Buffalo" ? "buf" : "cow";

    const fatReq =
      sankMode === "FAT_SNF" ||
      sankMode === "FAT_ONLY" ||
      sankMode === "FAT_CLR" ||
      sankMode === "FAT_CLR_AUTO_SNF";
    const snfReq = sankMode === "FAT_SNF" || sankMode === "FAT_CLR_AUTO_SNF";
    const digriReq =
      sankMode === "CLR_ONLY" ||
      sankMode === "FAT_CLR" ||
      sankMode === "FAT_CLR_AUTO_SNF";

    const fatOk = !fatReq || (String(fFat).trim() !== "" && Number(fFat) > 0);
    const snfOk = !snfReq || (String(fSnf).trim() !== "" && Number(fSnf) > 0);
    const digriOk =
      !digriReq || (String(fDigri).trim() !== "" && Number(fDigri) > 0);

    if (!fatOk || !snfOk || !digriOk) {
      setFRate("");
      setRateExplain("");
      return;
    }

    if (!rates?.ready) {
      setFRate("");
      setRateExplain("");
      return;
    }

    // per-customer chart selection (defaults to 1)
    const chartNo =
      customers?.ready && kn ? customers.getChartNoFor(kn, milkType) : 1;

    const base = rates.getRate({
      type: milkType,
      fat: fFat,
      snf: fSnf,
      digri: fDigri,
      ratechartno: chartNo,
    });

    if (base == null) {
      setFRate("");
      setRateExplain("");
      return;
    }

    const shiftExtra = 0;
    const finalRate = Number(base) + Number(shiftExtra);
    setFRate(finalRate.toFixed(2));

    if (shiftExtra) {
      setRateExplain(
        `Rate Chart (${Number(base).toFixed(
          2
        )}) + Shift Rate(${shiftExtra}) = ${finalRate.toFixed(2)}`
      );
    } else {
      setRateExplain(`Rate Chart (${Number(base).toFixed(2)})`);
    }
  }, [addOpen, sankMode, fMilk, fKno, fFat, fSnf, fDigri, customers, rates]);

  const filteredRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const kno = String(r.kno || "").toLowerCase();
      const name = String(r.name || r.cname || "").toLowerCase();
      const time = String(r.time || "").toLowerCase();
      const type = String(r.type || "").toLowerCase();
      return (
        kno.includes(q) ||
        name.includes(q) ||
        time.includes(q) ||
        type.includes(q)
      );
    });
  }, [rows, tableSearch]);

  const acceptedCount = filteredRows.length;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const showingFrom = filteredRows.length === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + pageRows.length, filteredRows.length);

  const totals = useMemo(() => {
    let totL = 0;
    let totAmt = 0;

    let wFat = 0,
      wSnf = 0,
      wClr = 0,
      wRate = 0;

    for (const r of filteredRows) {
      const l = Number(r.liter || 0);
      const fat = Number(r.fat || 0);
      const snf = Number(r.snf || 0);
      const clr = Number(r.digri || 0);
      const rate = Number(r.rate || 0);
      const amt = Number(r.amt || 0);

      totL += l;
      totAmt += amt;

      wFat += fat * l;
      wSnf += snf * l;
      wClr += clr * l;
      wRate += rate * l;
    }

    const fatAvg = totL > 0 ? wFat / totL : 0;
    const snfAvg = totL > 0 ? wSnf / totL : 0;
    const clrAvg = totL > 0 ? wClr / totL : 0;
    const rateAvg = totL > 0 ? wRate / totL : 0;

    return { totL, totAmt, fatAvg, snfAvg, clrAvg, rateAvg };
  }, [filteredRows]);

  // ----------------------- UPDATE RATE (WEB) -----------------------
  function computeRateForRow(row) {
    if (!rates?.ready || !customers?.ready) return 0;

    const kn = String(row.kno || "").trim();
    if (!kn) return 0;

    const typeNorm = mapTypeForServer(row.type); // Cow/Buffalo
    const typeKey = typeNorm === "Buffalo" ? "buf" : "cow";

    let chartNo = 1;
    try {
      chartNo = customers.getChartNoFor?.(kn, typeKey) || 1;
    } catch {}

    const fat = safeD(row.fat);
    const snf = safeD(row.snf);
    const digri = safeD(row.digri || row.clr);

    if (!(fat > 0 && snf > 0)) return 0;

    // preferred signature
    const r1 = rates.getRate?.({
      type: typeKey,
      fat,
      snf,
      digri,
      ratechartno: chartNo,
    });
    const n1 = Number(r1);
    if (Number.isFinite(n1) && n1 > 0) return n1;

    // fallback signature
    const r2 = rates.getRate?.(typeKey, fat, snf);
    const n2 = Number(r2);
    if (Number.isFinite(n2) && n2 > 0) return n2;

    return 0;
  }

  function rowDateIsoGuess(row) {
    const s = String(row?.date || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return dmyToIso(s);
    // list is for selected "date", so use that
    return dmyToIso(date);
  }

  async function onConfirmUpdateRates() {
    try {
      if (!filteredRows || filteredRows.length === 0) {
        alert("No records");
        setRateDlgOpen(false);
        return;
      }

      if (!rates?.ready) {
        alert("Rates not ready");
        return;
      }

      setRateDlgBusy(true);

      const items = [];
      let canCompute = 0;

      for (const r of filteredRows) {
        const liter = safeD(r.liter);
        const rateVal = computeRateForRow(r);
        if (!(liter > 0 && rateVal > 0)) continue;

        const amt = round2(liter * rateVal);

        items.push({
          kno: String(r.kno || "").trim(),
          date: rowDateIsoGuess(r),              // yyyy-mm-dd
          time: mapTimeForServer(r.time),        // Morning/Evening
          type: mapTypeForServer(r.type),        // Cow/Buffalo
          rate: rateVal.toFixed(2),
          amt: amt.toFixed(2),
        });

        canCompute++;
      }

      if (canCompute === 0) {
        alert("Rate not found for current rate card.");
        return;
      }

      const resp = await applySanklanRates({
        mainuser,
        itemsJson: JSON.stringify(items),
      });

      alert(`Updated: ${resp.updated || 0}, Skipped: ${resp.skipped || 0}`);
      setRateDlgOpen(false);

      await loadData(); // refresh
    } catch (e) {
      alert(e?.message || "Failed");
    } finally {
      setRateDlgBusy(false);
    }
  }

  // ----------------------- SAVE / UPDATE (WEB) -----------------------
  function existsDuplicateLocal(kn, timeKey, typeKey, dmy, ignorePk) {
    const d = String(dmy || "").trim();
    const k = String(kn || "").trim();
    if (!d || !k) return false;

    return rows.some((r) => {
      const rk = String(r.kno || "").trim();
      const rd = normalizeDmy(r.date);
      const rt = normalizeTimeTitle(r.time).toLowerCase();
      const rtype = normalizeTypeTitle(r.type).toLowerCase();

      const same =
        rk === k &&
        rd === d &&
        rt === String(timeKey).toLowerCase() &&
        rtype === String(typeKey).toLowerCase();

      if (!same) return false;

      // ignore the row we are editing
      if (ignorePk) {
        const igSame =
          rk === String(ignorePk.kno || "").trim() &&
          rd === String(ignorePk.date || "").trim() &&
          rt === String(ignorePk.time || "").toLowerCase() &&
          rtype === String(ignorePk.type || "").toLowerCase();

        if (igSame) return false;
      }

      return true;
    });
  }

  function validateBySankTypeWeb() {
    const kn = String(fKno || "").trim();
    if (!kn) {
      alert("Enter Customer Code");
      focusKey("kno");
      return false;
    }

    if (!String(fCname || "").trim()) {
      alert("Select valid customer (name not found)");
      focusKey("kno");
      return false;
    }

    const liter = Number(fLiter);
    if (!Number.isFinite(liter) || liter <= 0) {
      alert("Enter Liter");
      focusKey("liter");
      return false;
    }

    if (showFat) {
      const fat = Number(fFat);
      if (!Number.isFinite(fat) || fat <= 0) {
        alert("Enter FAT");
        focusKey("fat");
        return false;
      }
    }

    if (showDigri) {
      const clr = Number(fDigri);
      if (!Number.isFinite(clr) || clr <= 0) {
        alert("Enter CLR");
        focusKey("digri");
        return false;
      }
    }

    if (showSnf) {
      const snf = Number(fSnf);
      if (!Number.isFinite(snf) || snf <= 0) {
        alert("Enter SNF");
        focusKey("snf");
        return false;
      }
    }

    const rate = Number(fRate);
    if (!Number.isFinite(rate) || rate <= 0) {
      alert("Rate is missing");
      focusKey(rateEditable ? "rate" : "save");
      return false;
    }

    return true;
  }

  async function saveSanklanWeb() {
    if (saving) return;

    const kn = String(fKno || "").trim();
    const timeKey = mapTimeForServer(fTime); // Morning/Evening
    const typeKey = mapTypeForServer(fMilk); // Cow/Buffalo

    const isEdit = !!editMode && !!editPk;

    // duplicate check (ignore current row if editing)
    if (
      String(fDate).trim() &&
      existsDuplicateLocal(kn, timeKey, typeKey, fDate, isEdit ? editPk : null)
    ) {
      alert("Entry with the same values already exists.");
      focusKey("kno");
      return;
    }

    if (!validateBySankTypeWeb()) return;

    // block future date
    const iso = dmyToIso(fDate);
    if (iso && isFutureIso(iso)) {
      alert("Future date is not allowed");
      return;
    }

    const fatSend = showFat ? numStrOrZero(fFat) : "0";
    const snfSend = showSnf ? numStrOrZero(fSnf) : "0";
    const digriSend = showDigri ? numStrOrZero(fDigri) : "0";

    const literSend = numStrOrZero(fLiter);
    const rateSend = numStrOrZero(fRate);

    const amt = (Number(literSend) * Number(rateSend)).toFixed(2);

    const mainsettings = localStorage.getItem("mainsettings") || "";
    const msgonoff = localStorage.getItem("msgonoff") || "0";
    const mobileuser = String(mainuser || "");
    const dateSend = String(fDate || "").trim();

    const pkPayload = isEdit
      ? {
          pk_kno: String(editPk.kno || "").trim(),
          pk_date: String(editPk.date || "").trim(),
          pk_time: String(editPk.time || "").trim(),
          pk_type: String(editPk.type || "").trim(),
        }
      : {};

    setSaving(true);
    try {
      const text = await saveSanklan({
        mainuser: String(mainuser || ""),
        date: dateSend,
        date1: dateSend,
        date2: dateSend,

        type: typeKey,
        time: timeKey,
        kno: kn,

        liter: String(literSend),
        fat: String(fatSend),
        digri: String(digriSend),
        snf: String(snfSend),
        rate: String(rateSend),
        amt: String(amt),

        sredit: "0",
        setting: "-",
        mainsettings: String(mainsettings),
        msgonoff: String(msgonoff),
        mobileuser: String(mobileuser),

        flag: isEdit ? "edit" : "new",
        ...pkPayload,
      });

      if (text.includes("Successfully Saved.")) {
        // no dialog
        setFKno("");
        setFCname("");
        setFSample("1");
        setFLiter("");
        setFFat("");
        setFSnf("");
        setFDigri("");
        setFRate("");
        setRateExplain("");

        setEditMode(false);
        setEditPk(null);

        setView("list");
        await loadData();
        return;
      }

      if (text.includes("Successfully updated.")) {
        // no dialog
        setEditMode(false);
        setEditPk(null);

        setView("list");
        await loadData();
        return;
      }

      if (
        text.trim() === "Entry with the same values already exists." ||
        text.includes("already exists")
      ) {
        alert("Entry with the same values already exists.");
        focusKey("kno");
        return;
      }

      alert(text || "Unknown server response");
    } catch (e) {
      const http = e?.response?.status;
      const server = e?.response?.data;
      if (http) {
        alert(
          `Save failed (HTTP ${http}).\nServer: ${
            typeof server === "string" ? server : JSON.stringify(server)
          }`
        );
      } else {
        alert(`Network error: ${e?.message || e}`);
      }
    } finally {
      setSaving(false);
    }
  }

  // --------------------------- ADD VIEW ---------------------------
  if (addOpen) {
    return (
      <div className="mdPage">
        <div className="mdPageTop">
          <div className="mdPageTitle">
            <button
              type="button"
              className="mdIconBtn"
              onClick={closeAdd}
              title="Back"
              style={{ marginRight: 8 }}
            >
              ←
            </button>
            <span>{editMode ? "Edit Collection" : "Add Collection"}</span>
          </div>

          <div className="mdPageActions">
            <button type="button" className="mdLinkBtn">
              ↻ Refresh Rate Chart
            </button>
          </div>
        </div>

        <div className="mdCard">
          <div className="mdCardHead">
            <div className="mdCardHeadLeft">
              <div className="mdCardCaption">
                {editMode ? "Edit Collection" : "Add Collection"}
              </div>
            </div>
          </div>

          {/* ✅ one class applies yellow bg to all fields */}
          <div className={`mdModalBody ${editMode ? "mdEditMode" : ""}`}>
            <div className="mdAddTopRow">
              <div className="mdAddField">
                <div className="mdAddLabel">Date:</div>
                <input
                  className="mdInput"
                  type="date"
                  value={dmyToIso(fDate)}
                  max={todayIsoLocal()}
                  disabled={editMode}
                  onChange={(e) => {
                    const iso = e.target.value;
                    if (isFutureIso(iso)) return;
                    setFDate(isoToDmy(iso));
                  }}
                />
              </div>

              <div className="mdAddField">
                <div className="mdAddLabel"> </div>
                <div className="mdToggleGroup">
                  <button
                    type="button"
                    disabled={editMode}
                    className={`mdToggleBtn ${fTime === "Morning" ? "isOn" : ""}`}
                    onClick={() => !editMode && setFTime("Morning")}
                  >
                    ☀ Morning
                  </button>
                  <button
                    type="button"
                    disabled={editMode}
                    className={`mdToggleBtn ${fTime === "Evening" ? "isOn" : ""}`}
                    onClick={() => !editMode && setFTime("Evening")}
                  >
                    ☾ Evening
                  </button>
                </div>
              </div>

              <div className="mdAddField">
                <div className="mdAddLabel">Milk:</div>
                <div className="mdToggleGroup">
                  <button
                    type="button"
                    disabled={editMode}
                    className={`mdToggleBtn ${fMilk === "Cow" ? "isOn" : ""}`}
                    onClick={() => !editMode && setFMilk("Cow")}
                  >
                    🐄 Cow
                  </button>
                  <button
                    type="button"
                    disabled={editMode}
                    className={`mdToggleBtn ${fMilk === "Buffalo" ? "isOn" : ""}`}
                    onClick={() => !editMode && setFMilk("Buffalo")}
                  >
                    🐃 Buffalo
                  </button>
                </div>
              </div>
            </div>

            <div className="mdAddGrid">
              <div className="mdAddCell">
                <div className="mdAddLabel">Customer Code</div>
                <div className="mdInputWithIcon">
                  <input
                    ref={knoRef}
                    className="mdInput mdInputFull"
                    value={fKno}
                    readOnly={editMode}
                    onChange={(e) => setFKno(e.target.value)}
                    onKeyDown={(e) => onEnterAdvance(e, "kno")}
                    placeholder="Customer Code"
                    title={editMode ? "Customer Code is locked in Edit mode" : ""}
                  />
                  <button
                    type="button"
                    className="mdIconMini"
                    title="Search"
                    disabled={editMode}
                  >
                    🔍
                  </button>
                </div>
                <div className="mdHintText">{fCname || "\u00A0"}</div>
              </div>

              <div className="mdAddCell">
                <div className="mdAddLabel">Sample *</div>
                <select
                  ref={sampleRef}
                  className="mdSelect mdInputFull"
                  value={fSample}
                  onChange={(e) => setFSample(e.target.value)}
                  onKeyDown={(e) => onEnterAdvance(e, "sample")}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </div>

              <div className="mdAddCell">
                <div className="mdAddLabel">Liter *</div>
                <input
                  ref={literRef}
                  className="mdInput mdInputFull"
                  value={fLiter}
                  onChange={(e) => setFLiter(e.target.value)}
                  onKeyDown={(e) => onEnterAdvance(e, "liter")}
                  placeholder="0"
                />
              </div>

              {showFat && (
                <div className="mdAddCell">
                  <div className="mdAddLabel">FAT *</div>
                  <input
                    ref={fatRef}
                    className="mdInput mdInputFull"
                    value={fFat}
                    onChange={(e) => setFFat(e.target.value)}
                    onKeyDown={(e) => onEnterAdvance(e, "fat")}
                    placeholder="0.0"
                  />
                </div>
              )}

              {showDigri && (
                <div className="mdAddCell">
                  <div className="mdAddLabel">CLR *</div>
                  <input
                    ref={digriRef}
                    className="mdInput mdInputFull"
                    value={fDigri}
                    onChange={(e) => setFDigri(e.target.value)}
                    onKeyDown={(e) => onEnterAdvance(e, "digri")}
                    placeholder="0.0"
                  />
                </div>
              )}

              {showSnf && (
                <div className="mdAddCell">
                  <div className="mdAddLabel">SNF *</div>
                  <input
                    ref={snfRef}
                    className="mdInput mdInputFull"
                    value={fSnf}
                    onChange={(e) => setFSnf(e.target.value)}
                    onKeyDown={(e) => onEnterAdvance(e, "snf")}
                    placeholder="0.0"
                  />
                </div>
              )}

              <div className="mdAddCell">
                <div className="mdAddLabel">Rate *</div>
                <input
                  ref={rateRef}
                  className="mdInput mdInputFull"
                  value={fRate}
                  onChange={(e) => setFRate(e.target.value)}
                  onKeyDown={(e) => onEnterAdvance(e, "rate")}
                  placeholder="0.00"
                  readOnly={!rateEditable}
                />
              </div>
            </div>

            <div className="mdRateExplain">{rateExplain || "\u00A0"}</div>

            <div className="mdAddActions">
              <button
                type="button"
                className="mdBtnGhost"
                onClick={() => {
                  setFLiter("");
                  setFFat("");
                  setFSnf("");
                  setFDigri("");
                  setFRate("");
                  setRateExplain("");
                  setTimeout(() => focusKey("liter"), 0);
                }}
              >
                Reset
              </button>

              <button
                ref={saveBtnRef}
                type="button"
                className="mdBtnPrimary"
                onClick={saveSanklanWeb}
                disabled={saving}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveSanklanWeb();
                  }
                }}
              >
                {saving
                  ? editMode
                    ? "Updating..."
                    : "Saving..."
                  : editMode
                  ? "Update"
                  : "Save"}
              </button>
            </div>

            <div className="mdSmallNote">
              Customers ready: {customers?.ready ? "Yes" : "No"} • Rates ready:{" "}
              {rates?.ready ? "Yes" : "No"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (adjOpen) {
    return (
      <CollectionAdjustment
        mainuser={mainuser}
        selectedKendra={selectedKendra}
        onBack={closeAdjust}
      />
    );
  }

  // --------------------------- LIST VIEW ---------------------------
  return (
    <div className="mdPage">
      <div className="mdPageTop">
        <div className="mdPageTitle">
          <span className="mdPageTitleIcon">▣</span>
          <span>Collections</span>
        </div>

        <div className="mdPageActions">
          <button type="button" className="mdLinkBtn" onClick={openAdd}>
            + Add Collection
          </button>
          <div className="mdSep" />
          <button type="button" className="mdLinkBtn" onClick={openAdjust}>
            $ Collection adjustment
          </button>
          <div className="mdSep" />
          <button
            type="button"
            className="mdLinkBtn"
            onClick={() => setRateDlgOpen(true)}
          >
            ₹ Update Collection Rate
          </button>
          <div className="mdSep" />
        
        </div>
      </div>

      <div className="mdFilters">
        <div className="mdFilter">
          <span className="mdFilterLabel">Date :</span>
          <input
            className="mdInput"
            type="date"
            value={dmyToIso(date)}
            max={todayIsoLocal()}
            onChange={(e) => {
              const iso = e.target.value;
              if (isFutureIso(iso)) return;
              setDate(isoToDmy(iso));
            }}
          />
        </div>

        <div className="mdFilter">
          <span className="mdFilterLabel">Shift :</span>
          <select
            className="mdSelect"
            value={shift}
            onChange={(e) => setShift(e.target.value)}
          >
            <option>All</option>
            <option>Morning</option>
            <option>Evening</option>
          </select>
        </div>

        <div className="mdFilter">
          <span className="mdFilterLabel">Milk :</span>
          <select
            className="mdSelect"
            value={milk}
            onChange={(e) => setMilk(e.target.value)}
          >
            <option>All</option>
            <option>Cow</option>
            <option>Buffalo</option>
          </select>
        </div>

        <button
          type="button"
          className="mdSearchBtn"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div className="mdCard">
        <div className="mdCardHead">
          <div className="mdCardHeadLeft">
            <div className="mdCardCaption">Collections</div>
          </div>

          <div className="mdCardHeadMid">
            <div className="mdAccepted">
              Accepted: <b>{acceptedCount}</b>
            </div>
          </div>

          <div className="mdCardHeadRight">
            <div className="mdSearchBox">
              <span className="mdSearchLabel">Search</span>
              <input
                className="mdInput mdInputSm"
                value={tableSearch}
                onChange={(e) => {
                  setTableSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search"
              />
            </div>
          </div>
        </div>

        <div className="mdTableWrap">
          <table className="mdTable">
            <thead>
              <tr>
                <th className="mdCheckCol">
                  <input type="checkbox" disabled />
                </th>
                <th>Code</th>
                <th>Name</th>
                <th>Shift</th>
                <th>Milk</th>
                <th className="mdNum">Quantity</th>
                <th className="mdNum">FAT</th>
                <th className="mdNum">SNF</th>
                <th className="mdNum">CLR</th>
                <th className="mdNum">Rate</th>
                <th className="mdNum">Amount</th>
                <th className="mdActionCol">Actions</th>
              </tr>
            </thead>

            <tbody>
              {err ? (
                <tr>
                  <td colSpan={12} className="mdEmpty">
                    {err}
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="mdEmpty">
                    {loading ? "Loading..." : "No records"}
                  </td>
                </tr>
              ) : (
                <>
                  {pageRows.map((r, idx) => (
                    <tr key={`${r.kno}-${r.time}-${r.type}-${idx}`}>
                      <td className="mdCheckCol">
                        <input type="checkbox" />
                      </td>
                      <td>{r.kno}</td>
                      <td>{r.cname}</td>
                      <td>{shiftShort(r.time)}</td>
                      <td>{milkShort(r.type)}</td>
                      <td className="mdNum">{fmt2(r.liter)}</td>
                      <td className="mdNum">{fmt1(r.fat)}</td>
                      <td className="mdNum">{fmt1(r.snf)}</td>
                      <td className="mdNum">{fmt1(r.digri)}</td>
                      <td className="mdNum">{fmt2(r.rate)}</td>
                      <td className="mdNum">{fmt2(r.amt)}</td>
                      <td className="mdActionCol">
                        <button
                          className="mdIconBtn"
                          type="button"
                          title="Edit"
                          onClick={() => openEdit(r)}
                        >
                          ✎
                        </button>
                      </td>
                    </tr>
                  ))}

                  <tr className="mdTotalRow">
                    <td className="mdCheckCol" />
                    <td colSpan={2}>Total</td>
                    <td>-</td>
                    <td>-</td>
                    <td className="mdNum">{totals.totL.toFixed(2)}</td>
                    <td className="mdNum">{totals.fatAvg.toFixed(1)}</td>
                    <td className="mdNum">{totals.snfAvg.toFixed(1)}</td>
                    <td className="mdNum">{totals.clrAvg.toFixed(1)}</td>
                    <td className="mdNum">{totals.rateAvg.toFixed(2)}</td>
                    <td className="mdNum">{totals.totAmt.toFixed(2)}</td>
                    <td />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="mdTableFooter">
          <div className="mdShowing">
            Showing {showingFrom} to {showingTo} of {filteredRows.length} entries
          </div>

          <div className="mdPager">
            <button
              type="button"
              className="mdPagerBtn"
              onClick={() => setPage(1)}
              disabled={safePage <= 1}
            >
              ≪
            </button>
            <button
              type="button"
              className="mdPagerBtn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ‹
            </button>

            <div className="mdPagerPage">{safePage}</div>

            <button
              type="button"
              className="mdPagerBtn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              ›
            </button>
            <button
              type="button"
              className="mdPagerBtn"
              onClick={() => setPage(totalPages)}
              disabled={safePage >= totalPages}
            >
              ≫
            </button>

            <select
              className="mdSelect mdSelectSm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* ✅ Dialog for Update Collection Rate */}
      <AppDialog
        open={rateDlgOpen}
        title="Upadte Collection Rate"
        message="Are you sure to Upadte rate's for all collections with current rate card?"
        onClose={() => !rateDlgBusy && setRateDlgOpen(false)}
        onNo={() => !rateDlgBusy && setRateDlgOpen(false)}
        onYes={onConfirmUpdateRates}
        noText="No"
        yesText="Save"
        busy={rateDlgBusy}
      />
    </div>
  );
}
